<template>
  <BaseModal class="product-modal" v-model="isOpenModal">
    <template v-slot:header>
      <div class="product-header">
        <div class="product-header-main">
          <img
            v-if="productFull && productFull.part.mainImage"
            :src="productFull.part.mainImage"
            :alt="productFull.part.nkrCode"
            class="product-thumb"
          />
          <div v-else class="product-thumb is-empty">
            <i class="mdi mdi-image-off-outline"></i>
          </div>
          <div class="product-header-text">
            <div class="product-header-line">
              <h2 class="product-code">
                {{ $t("message.product_modal.part") }}
                <span
                  >#{{ productFull ? productFull.part.nkrCode : code }}</span
                >
              </h2>
              <span v-if="productFull" class="brand-pill">
                {{ productFull.part.brand }}
              </span>
              <span
                v-if="productFull"
                class="status-badge"
                :class="isEnabled ? 'tone-green' : 'tone-red'"
              >
                <span class="status-dot"></span>
                {{
                  isEnabled
                    ? $t("message.product_modal.general.enabled")
                    : $t("message.product_modal.general.disabled")
                }}
              </span>
              <span
                v-if="productFull && productFull.part.dependencia"
                class="meta-pill"
              >
                {{ productFull.part.dependencia }}
              </span>
            </div>
            <p
              v-if="productFull"
              class="product-title"
              :title="productFull.part.title"
            >
              {{ productFull.part.title }}
            </p>
          </div>
          <button
            v-if="productFull && userCanUseAIFeatures"
            type="button"
            class="pb-btn pb-btn-ai ai-toggle"
            :class="{ 'is-open': showAiPanel }"
            @click="showAiPanel = !showAiPanel"
          >
            <i class="mdi mdi-creation"></i>
            <span>AI</span>
          </button>
        </div>

        <!-- Pestañas: viven en el header para quedar fijas al hacer scroll -->
        <div v-if="productFull" class="product-tabs-wrap">
          <button
            v-show="tabsCanScrollLeft"
            type="button"
            class="tabs-scroll-btn is-left"
            aria-label="Scroll tabs left"
            tabindex="-1"
            @click="scrollTabs(-1)"
          >
            <i class="mdi mdi-chevron-left"></i>
          </button>
          <nav
            class="product-tabs"
            role="tablist"
            ref="tabsBar"
            @scroll.passive="updateTabsScroll"
            @wheel="onTabsWheel"
          >
            <button
              v-for="tab in productTabs"
              :key="tab.key"
              type="button"
              role="tab"
              class="product-tab"
              :class="{ 'is-active': selectedSection === tab.key }"
              :aria-selected="selectedSection === tab.key ? 'true' : 'false'"
              @click="selectTab(tab.key)"
            >
              <i class="mdi" :class="tab.icon"></i>
              {{ tab.label }}
            </button>
          </nav>
          <button
            v-show="tabsCanScrollRight"
            type="button"
            class="tabs-scroll-btn is-right"
            aria-label="Scroll tabs right"
            tabindex="-1"
            @click="scrollTabs(1)"
          >
            <i class="mdi mdi-chevron-right"></i>
          </button>
        </div>
      </div>
    </template>

    <div v-if="notFound" class="product-state">
      <div class="state-icon is-error">
        <i class="mdi mdi-database-alert-outline"></i>
      </div>
      <h3 class="state-title">Product not found in master catalog</h3>
      <p class="state-text">
        <strong>{{ code }}</strong> exists locally but was not created in the
        master catalog (the main admin creation likely failed). It cannot be
        used until it exists in the master.
      </p>
      <button
        type="button"
        class="pb-btn pb-btn-danger"
        :disabled="deletingOrphan"
        @click="deleteOrphanProduct"
      >
        <i class="mdi mdi-delete-outline"></i>
        <span v-if="!deletingOrphan">Delete orphaned local product</span>
        <span v-else>Deleting...</span>
      </button>
    </div>

    <div class="product-view relative" v-else-if="productFull && !processing">
      <div v-if="!isEnabled" class="product-alert is-error">
        <i class="mdi mdi-alert-circle-outline"></i>
        <span>{{ $t("message.product_modal.disabled_part") }}</span>
      </div>
      <div class="product-content">
        <!-- main section - box -->
        <template v-if="selectedSection == 'general'">
          <!-- Acciones: edición, etiquetas y venta perdida -->
          <div class="product-toolbar">
            <div class="toolbar-group">
              <template v-if="userCanEditProducts">
                <template v-if="editPart">
                  <button
                    type="button"
                    class="pb-btn pb-btn-success"
                    :disabled="!partIsModified"
                    @click="saveMainInfo()"
                  >
                    <i class="mdi mdi-content-save-outline"></i>
                    {{ $t("message.product_modal.general.save") }}
                  </button>
                  <button
                    type="button"
                    class="pb-btn"
                    @click="revertMainInfo()"
                  >
                    {{ $t("message.product_modal.general.cancel") }}
                  </button>
                </template>
                <button
                  v-else
                  type="button"
                  class="pb-btn pb-btn-primary"
                  @click="editPart = true"
                >
                  <i class="mdi mdi-pencil-outline"></i>
                  {{ $t("message.product_modal.general.edit") }}
                </button>
              </template>
              <label v-if="userCanEditProducts" class="status-toggle">
                <BaseSwitchCustom
                  :value="productFull.part.enabled"
                  @change="
                    updateProductStatus(productFull.part.nkrCode, !isEnabled)
                  "
                />
                <span :class="isEnabled ? 'is-on' : 'is-off'">
                  {{
                    isEnabled
                      ? $t("message.product_modal.general.enabled")
                      : $t("message.product_modal.general.disabled")
                  }}
                </span>
              </label>
            </div>
            <div class="toolbar-group toolbar-end">
              <div class="label-group" role="group">
                <span class="label-group-title">
                  <i class="mdi mdi-printer-outline"></i>
                  <span>{{ $t("message.product_modal.general.label") }}</span>
                </span>
                <button
                  v-for="size in labelSizes"
                  :key="size.code"
                  type="button"
                  :title="size.title"
                  @click="openLabelGenerator(size.code)"
                >
                  {{ size.code }}
                </button>
                <button
                  type="button"
                  :title="$t('message.product_modal.general.label_generator')"
                  @click="openLabelGenerator()"
                >
                  <i class="mdi mdi-open-in-new"></i>
                </button>
              </div>
              <button
                v-if="!partIsModified"
                type="button"
                class="pb-btn pb-btn-warning"
                @click="showRestockModal = true"
              >
                <i class="mdi mdi-cart-remove"></i>
                {{ $t("message.product_modal.general.report_lost_sale") }}
              </button>
            </div>
          </div>

          <!-- general info -->
          <div class="box w-full">
            <div class="box-header">
              {{ $t("message.product_modal.general.general_info") }}
              <span v-if="editPart" class="editing-pill">
                <i class="mdi mdi-pencil"></i>
                {{ $t("message.product_modal.general.editing") }}
              </span>
            </div>

            <!-- Request Restock Modal -->
            <ReportLostSaleModal
              :show="showRestockModal"
              :productCode="productFull.part.nkrCode"
              :productDescription="productFull.part.title"
              :productImage="productFull.part.mainImage"
              :currentStock="totalStock"
              :departmentId="activeDepartmentId?.departamentoId || ''"
              @close="showRestockModal = false"
              @success="handleRestockSuccess"
            />
            <div class="flex flex-col md:flex-row">
              <div class="w-full md:w-4/6 box-content-block py-2">
                <div class="info-field mb-2">
                  <span class="info-label">Infoauto title</span>
                  <span class="info-value text-gray-500 italic">{{
                    productFull.part.description
                  }}</span>
                </div>
                <template v-if="!editPart">
                  <table class="product-lines-table mb-3">
                    <tbody>
                      <tr>
                        <th>{{ $t("message.product_modal.general.title") }}</th>
                        <td>
                          {{ productFull.part.title }}
                          <span class="info-meta"
                            >({{ productFull.part.title.length }} chars)</span
                          >
                        </td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t("message.product_modal.general.title_std") }}
                        </th>
                        <td>{{ productFull.part.titleStd || "-" }}</td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t("message.product_modal.general.title_alt") }}
                        </th>
                        <td>{{ productFull.part.titleAlt || "-" }}</td>
                      </tr>
                      <tr>
                        <th>{{ $t("message.product_modal.general.oe") }}</th>
                        <td>
                          {{ productFull.part.oe || "-" }}
                          <button
                            v-if="productFull.part.oe"
                            @click="copyToClipboard(productFull.part.oe)"
                            class="copy-btn"
                            title="Copy"
                          >
                            ⎘
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t("message.product_modal.general.unificado") }}
                        </th>
                        <td>
                          {{ productFull.part.unificado || "-" }}
                          <button
                            v-if="productFull.part.unificado"
                            @click="copyToClipboard(productFull.part.unificado)"
                            class="copy-btn"
                            title="Copy"
                          >
                            ⎘
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
                <template v-if="editPart">
                  <div class="input-item flex flex-col gap-2 mb-2">
                    <label for="title"
                      >{{ $t("message.product_modal.general.title") }} ({{
                        productFull.part.title.length
                      }}
                      chars)</label
                    >
                    <input
                      id="title"
                      type="text"
                      class="regular clear"
                      v-model="productFull.part.title"
                    />
                    <div class="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        name="title-unificado"
                        id="title-unificado"
                        v-model="titleUpdateSimilar"
                      />
                      <label for="title-unificado">{{
                        $t("message.product_modal.general.update_similar")
                      }}</label>
                    </div>
                  </div>
                </template>
                <template v-if="editPart">
                  <div class="input-item flex flex-col gap-2 mb-2">
                    <label for="titleSTD">{{
                      $t("message.product_modal.general.title_std")
                    }}</label>
                    <input
                      id="titleSTD"
                      type="text"
                      class="regular clear"
                      v-model="productFull.part.titleStd"
                    />
                    <div class="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        name="title-std-unificado"
                        id="title-std-unificado"
                        v-model="titleStdUpdateSimilar"
                      />
                      <label for="title-std-unificado">{{
                        $t("message.product_modal.general.update_similar")
                      }}</label>
                    </div>
                  </div>
                </template>
                <template v-if="editPart">
                  <div class="input-item flex flex-col gap-2 mb-2">
                    <label for="titleAlt">{{
                      $t("message.product_modal.general.title_alt")
                    }}</label>
                    <input
                      id="titleAlt"
                      type="text"
                      class="regular clear"
                      v-model="productFull.part.titleAlt"
                    />
                    <div class="flex items-center gap-2 mt-1">
                      <input
                        type="checkbox"
                        name="title-alt-unificado"
                        id="title-alt-unificado"
                        v-model="titleAltUpdateSimilar"
                      />
                      <label for="title-alt-unificado">{{
                        $t("message.product_modal.general.update_similar")
                      }}</label>
                    </div>
                  </div>
                </template>
                <div class="input-item">
                  <template v-if="keywordsDestructured">
                    <label>
                      {{ $t("message.product_modal.general.keywords") }} ({{
                        keywordsDestructured.length
                      }})
                    </label>
                    <div
                      class="badge-list"
                      v-if="keywordsDestructured.length > 0"
                    >
                      <label
                        v-for="keyword in keywordsDestructured"
                        :key="keyword"
                      >
                        <span class="keyword-chip">
                          {{ keyword }}
                          <button
                            v-if="editPart"
                            type="button"
                            class="keyword-remove"
                            :aria-label="`Remove ${keyword}`"
                            @click="removeKeyword(keyword)"
                          >
                            <i class="mdi mdi-close"></i>
                          </button>
                        </span>
                      </label>
                    </div>
                  </template>
                  <template v-if="editPart">
                    <input
                      type="text"
                      class="regular clear"
                      name="keyword"
                      id="keyword-input"
                      enterkeyhint="done"
                      :placeholder="
                        $t('message.product_modal.general.add_keyword')
                      "
                      v-if="editPart"
                      v-on:keyup.enter="addKeyword()"
                      list="keywordsOptionsDataList"
                    />
                  </template>
                  <datalist id="keywordsOptionsDataList">
                    <option v-for="key in keywordsOptions" :key="key">{{
                      key.keyword
                    }}</option>
                  </datalist>
                </div>
                <template v-if="editPart">
                  <div class="grid grid-cols-1 sm:grid-cols-2 mt-2 gap-2">
                    <div class="flex flex-col">
                      <label for="oe">{{
                        $t("message.product_modal.general.oe")
                      }}</label>
                      <input
                        type="text"
                        class="regular clear"
                        name="oe"
                        id="oe"
                        v-model="productFull.part.oe"
                      />
                    </div>
                    <div class="flex flex-col">
                      <label for="unificado">{{
                        $t("message.product_modal.general.unificado")
                      }}</label>
                      <input
                        type="text"
                        class="regular clear"
                        name="unificado"
                        id="unificado"
                        v-model="productFull.part.unificado"
                      />
                      <div
                        class="flex items-center gap-2 mt-2"
                        v-if="similarParts.length > 0"
                      >
                        <input
                          type="checkbox"
                          name="similar-unificado"
                          id="similar-unificado"
                          v-model="unificadoUpdateSimilar"
                        />
                        <label for="similar-unificado">{{
                          $t(
                            "message.product_modal.general.update_others_same_unificado"
                          )
                        }}</label>
                      </div>
                    </div>
                  </div>
                </template>
                <div>
                  <button
                    v-if="similarParts.length > 0"
                    type="button"
                    class="pb-link"
                    :aria-expanded="showSimilarParts ? 'true' : 'false'"
                    @click="showSimilarParts = !showSimilarParts"
                  >
                    <i
                      class="mdi"
                      :class="
                        showSimilarParts ? 'mdi-chevron-up' : 'mdi-chevron-down'
                      "
                    ></i>
                    {{
                      showSimilarParts
                        ? $t("message.product_modal.general.close")
                        : $t(
                            "message.product_modal.general.show_similar_products"
                          )
                    }}
                    <span class="count-pill">{{ similarParts.length }}</span>
                  </button>
                  <div
                    id="similar-products"
                    class="similar-products"
                    v-if="similarParts.length > 0 && showSimilarParts"
                  >
                    <div
                      class="box-content-block"
                      v-for="product in productFull.similarParts"
                      :key="product.nkrCode"
                    >
                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          class="similar-code"
                          @click="openProduct(product.nkrCode)"
                        >
                          {{ `${product.nkrCode} - ${product.brand}` }}
                        </button>
                        <button
                          @click="copyToClipboard(product.nkrCode)"
                          class="copy-btn"
                          title="Copy SKU"
                        >
                          ⎘
                        </button>
                        <span v-if="product.oe" class="similar-meta">
                          OE: <span class="font-mono">{{ product.oe }}</span>
                          <button
                            @click="copyToClipboard(product.oe)"
                            class="copy-btn"
                            title="Copy OE"
                          >
                            ⎘
                          </button>
                        </span>
                        <span v-if="product.unificado" class="similar-meta">
                          Unif:
                          <span class="font-mono">{{ product.unificado }}</span>
                          <button
                            @click="copyToClipboard(product.unificado)"
                            class="copy-btn"
                            title="Copy Unificado"
                          >
                            ⎘
                          </button>
                        </span>
                      </div>
                      <p class="similar-description">
                        {{ product.description }}
                      </p>
                      <div
                        class="w-full overflow-x-auto similar-products-table-wrapper"
                        v-if="product.stockAndPrice.length > 0"
                      >
                        <table class="tiny-table similar-products-table">
                          <tr>
                            <th>
                              {{ $t("message.product_modal.general.line") }}
                            </th>
                            <th>
                              {{ $t("message.product_modal.general.online") }}
                            </th>
                            <th>
                              {{
                                $t("message.product_modal.general.repair_shop")
                              }}
                            </th>
                            <th>
                              {{
                                $t("message.product_modal.general.wholesale")
                              }}
                            </th>
                            <th v-if="userCanSeePrecio4">
                              {{ $t("message.product_modal.general.export") }}
                            </th>
                            <th v-if="userCanSeePrecio5">
                              {{ $t("message.product_modal.general.gruponkr") }}
                            </th>
                            <th v-if="userCanSeePrecio6">
                              {{ $t("message.product_modal.general.wh2") }}
                            </th>
                            <th>
                              {{
                                $t(
                                  "message.product_modal.general.qty_available"
                                )
                              }}
                            </th>
                            <th>
                              {{
                                $t("message.product_modal.general.warehouse")
                              }}
                            </th>
                          </tr>
                          <tr
                            v-for="(entry, index) in product.stockAndPrice"
                            :key="index"
                          >
                            <td>{{ entry.line }}</td>
                            <td>
                              {{ entry.online | currencyFormatted(true) }}
                            </td>
                            <td>
                              {{ entry.repairShop | currencyFormatted(true) }}
                            </td>
                            <td>
                              {{ entry.wholesale | currencyFormatted(true) }}
                            </td>
                            <td v-if="userCanSeePrecio4">
                              {{ entry.precio4 | currencyFormatted(true) }}
                            </td>
                            <td v-if="userCanSeePrecio5">
                              {{ entry.precio5 | currencyFormatted(true) }}
                            </td>
                            <td v-if="userCanSeePrecio6">
                              {{ entry.precio6 | currencyFormatted(true) }}
                            </td>
                            <td>{{ entry.stock }}</td>
                            <td>{{ entry.warehouse }}</td>
                          </tr>
                        </table>
                      </div>
                      <div v-else class="similar-empty">
                        {{ $t("message.product_modal.general.not_available") }}
                      </div>
                    </div>
                  </div>
                </div>
                <h3 class="section-title">
                  {{ $t("message.product_modal.general.stock_and_price") }}
                  <span>{{ productFull.part.nkrCode }}</span>
                </h3>
                <ProductStockAndPrice
                  v-if="!isCatalogDependencia"
                  :product="productFull"
                />

                <div
                  v-for="core in productFull.cores || []"
                  :key="`core-${core.nkrCode}`"
                  class="mt-4"
                >
                  <h3 class="section-title">
                    {{ $t("message.product_modal.general.core") }}
                    <button
                      type="button"
                      class="similar-code"
                      @click="openProduct(core.nkrCode)"
                    >
                      {{ core.nkrCode }}
                    </button>
                    <button
                      @click="copyToClipboard(core.nkrCode)"
                      class="copy-btn"
                      title="Copy SKU"
                    >
                      ⎘
                    </button>
                  </h3>
                  <p v-if="core.description" class="similar-description">
                    {{ core.description }}
                    <span v-if="core.brand">· {{ core.brand }}</span>
                  </p>
                  <ProductStockAndPrice :product="core" />
                </div>
                <p v-if="productFull.part.coreOf" class="mt-2">
                  {{ $t("message.product_modal.general.core_of") }}
                  <button
                    type="button"
                    class="similar-code"
                    @click="openProduct(productFull.part.coreOf)"
                  >
                    {{ productFull.part.coreOf }}
                  </button>
                </p>

                <div v-if="isCatalogDependencia" class="catalog-price-block">
                  <!-- Vista de solo lectura: igual que un producto regular -->
                  <div v-if="!editPart" class="catalog-readonly">
                    <div class="catalog-meta">
                      <span> <b>Logic:</b> {{ catalogDraft.priceLogic }} </span>
                      <span>
                        <b>Price group:</b> {{ catalogDraft.priceGroup || "—" }}
                      </span>
                    </div>
                    <div class="w-full overflow-x-auto mt-2">
                      <table class="tiny-table catalog-table">
                        <tr>
                          <th>Line</th>
                          <th>Price Online</th>
                          <th>Price Repair Shop</th>
                          <th>Price wholesale</th>
                          <th v-if="userCanSeePrecio4">Price Export</th>
                          <th v-if="userCanSeePrecio5">Price GrupoNKR</th>
                          <th v-if="userCanSeePrecio6">Price WH2</th>
                        </tr>
                        <tr class="catalog-price-row">
                          <td>{{ productFull.part.dependencia }}</td>
                          <td>
                            {{ catalogDraft.online | currencyFormatted(true) }}
                          </td>
                          <td>
                            {{
                              catalogDraft.repairShop | currencyFormatted(true)
                            }}
                          </td>
                          <td>
                            {{
                              catalogDraft.wholesale | currencyFormatted(true)
                            }}
                          </td>
                          <td v-if="userCanSeePrecio4">
                            {{ catalogDraft.precio4 | currencyFormatted(true) }}
                          </td>
                          <td v-if="userCanSeePrecio5">
                            {{ catalogDraft.precio5 | currencyFormatted(true) }}
                          </td>
                          <td v-if="userCanSeePrecio6">
                            {{ catalogDraft.precio6 | currencyFormatted(true) }}
                          </td>
                        </tr>
                        <tr class="catalog-min-row">
                          <td>Min</td>
                          <td>
                            {{
                              catalogDraft.onlineMinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                          <td>
                            {{
                              catalogDraft.repairShopMinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                          <td>
                            {{
                              catalogDraft.wholesaleMinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                          <td v-if="userCanSeePrecio4">
                            {{
                              catalogDraft.precio4MinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                          <td v-if="userCanSeePrecio5">
                            {{
                              catalogDraft.precio5MinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                          <td v-if="userCanSeePrecio6">
                            {{
                              catalogDraft.precio6MinPrice
                                | currencyFormatted(true)
                            }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>

                  <!-- Modo edición: editor completo de catálogo -->
                  <template v-else>
                    <CatalogPriceForm
                      v-model="catalogDraft"
                      :priceGroups="priceGroups"
                      :masterListPrice="Number(productFull.part.listPrice) || 0"
                      :dealerProfit="dealerProfit"
                    />
                    <button
                      v-if="userCanEditProducts"
                      class="btn-info-button px-8 mt-2"
                      :disabled="savingCatalog"
                      @click="saveCatalogDraft"
                    >
                      <span v-if="!savingCatalog">Save catalog price</span>
                      <span v-else>Saving...</span>
                    </button>
                  </template>
                </div>

                <div v-if="productFull.part.pack === 'KIT'" class="kit-summary">
                  <p>
                    {{
                      $t(
                        "message.product_modal.general.price_without_discount"
                      )
                    }}:
                    {{
                      productFull.part.kitData.priceNoDiscount
                        | currencyFormatted(true)
                    }}
                  </p>
                  <p>
                    {{
                      $t("message.product_modal.general.price_with_discount")
                    }}:
                    <b>{{
                      productFull.part.kitData.price | currencyFormatted(true)
                    }}</b>
                  </p>
                  <p>
                    {{
                      $t("message.product_modal.general.available_for_sale")
                    }}: <b>{{ productFull.part.kitData.stock }}</b>
                    {{ $t("message.product_modal.general.kits") }}
                  </p>
                </div>

                <div class="mt-4">
                  <h3 class="section-title">
                    {{ $t("message.product_modal.general.other_stores") }}
                  </h3>
                  <div class="table-scroll">
                    <table class="default-table">
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t("message.product_modal.general.store") }}
                          </th>
                          <th class="text-left">
                            {{ $t("message.product_modal.general.title") }}
                          </th>
                          <th>
                            {{ $t("message.product_modal.general.brand") }}
                          </th>
                          <th>
                            {{ $t("message.product_modal.general.stock") }}
                          </th>
                          <th>
                            {{ $t("message.product_modal.general.status") }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="storeStatus in productFull.storesEnabled"
                          :key="storeStatus.code"
                        >
                          <td>{{ storeStatus.name }}</td>
                          <td>
                            <div class="flex flex-col gap-2">
                              <div>
                                {{ storeStatus.title }}
                              </div>
                              <div
                                v-if="
                                  storeStatus.description &&
                                    storeStatus.description !== ''
                                "
                              >
                                <b>Infoauto</b>: {{ storeStatus.description }}
                              </div>
                            </div>
                          </td>
                          <td class="text-center">{{ storeStatus.brand }}</td>
                          <td class="text-center">
                            {{ storeStatus.stock || 0 }}
                          </td>
                          <td
                            class="text-center"
                            :class="{
                              'text-green-500': storeStatus.active == 1,
                              'text-red-500': storeStatus.active == 0
                            }"
                          >
                            {{
                              storeStatus.active == 1
                                ? $t("message.product_modal.general.enabled")
                                : $t("message.product_modal.general.disabled")
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <template v-if="!editPart">
                  <h3 class="section-title">
                    {{ $t("message.product_modal.general.more_info") }}
                  </h3>
                  <div
                    v-if="
                      productFull.part.longDescription &&
                        productFull.part.longDescription !== ''
                    "
                    class="long-description"
                    v-html="productFull.part.longDescription"
                  ></div>
                  <div v-else class="product-alert is-warning is-inline">
                    <i class="mdi mdi-text-box-remove-outline"></i>
                    <span>
                      {{ $t("message.product_modal.general.no_description") }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  <div class="flex flex-col">
                    <label for="long-description">{{
                      $t("message.product_modal.general.more_info")
                    }}</label>
                    <VueEditor
                      name="long-description"
                      id="long-description"
                      class="regular bordered"
                      v-model="productFull.part.longDescription"
                    ></VueEditor>
                  </div>
                </template>
              </div>
              <div
                class="w-full md:w-2/6 py-2 box-content-block sidebar-details"
              >
                <div id="categories-summary" class="summary-table-block">
                  <label
                    >{{ $t("message.product_modal.general.categories") }}
                    <button
                      type="button"
                      class="icon-btn"
                      :title="$t('message.product_modal.general.edit')"
                      @click="selectTab('categories')"
                    >
                      <i class="mdi mdi-pencil-outline"></i></button
                  ></label>
                  <table class="summary-table mt-2">
                    <tbody>
                      <tr
                        v-for="category in productFull.categories"
                        :key="category.id"
                      >
                        <td>{{ category.categoryName }}</td>
                        <td>{{ category.subCategoryName }}</td>
                      </tr>
                      <tr v-if="!productFull.categories.length">
                        <td colspan="2" class="summary-empty">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <hr />
                <div class="py-2 summary-table-block" id="attributes-area">
                  <label
                    >{{ $t("message.product_modal.general.attributes") }}
                    <button
                      type="button"
                      class="icon-btn"
                      :title="$t('message.product_modal.general.edit')"
                      @click="selectTab('attributes')"
                    >
                      <i class="mdi mdi-pencil-outline"></i>
                    </button>
                  </label>
                  <table class="summary-table mt-2">
                    <tbody>
                      <tr
                        v-for="attribute in productFull.attributes"
                        :key="attribute.id"
                      >
                        <td>{{ attribute.attributeName }}</td>
                        <td>{{ attribute.value }}</td>
                      </tr>
                      <tr v-if="!productFull.attributes.length">
                        <td colspan="2" class="summary-empty">-</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <hr />
                <div class="py-2 summary-table-block" id="part-meta-summary">
                  <template v-if="!editPart">
                    <div class="info-field mt-2">
                      <span class="info-label">{{
                        $t("message.product_modal.general.origin")
                      }}</span>
                      <span class="info-value">{{
                        productFull.part.origin
                      }}</span>
                    </div>
                    <div class="info-field mt-2">
                      <span class="info-label">{{
                        $t("message.product_modal.general.package")
                      }}</span>
                      <span class="info-value">{{
                        productFull.part.pack
                      }}</span>
                    </div>
                    <div class="info-field mt-2">
                      <span class="info-label">{{
                        $t("message.product_modal.general.qty_sold")
                      }}</span>
                      <span class="info-value"
                        >{{ productFull.part.qtySold }}
                        <span class="info-meta"
                          >({{ $t("message.product_modal.general.unificado") }}
                          {{ productFull.part.qtySoldUnificado }})</span
                        ></span
                      >
                    </div>
                  </template>
                  <template v-else>
                    <div class="info-field mt-2">
                      <span class="info-label">{{
                        $t("message.product_modal.general.origin")
                      }}</span>
                      <span class="info-value">{{
                        productFull.part.origin
                      }}</span>
                    </div>
                    <div class="mt-2">
                      <BaseSelectField
                        v-model="productFull.part.pack"
                        :options="[
                          { name: 'PCS', code: 'PCS' },
                          { name: 'SET', code: 'SET' },
                          { name: 'KIT', code: 'KIT' },
                          { name: 'KITS - Infoauto', code: 'KITS' }
                        ]"
                        :label="
                          `${$t('message.product_modal.general.package')}: ${
                            productFull.part.pack
                          }`
                        "
                        required
                      />
                    </div>
                    <div class="info-field mt-2">
                      <span class="info-label">{{
                        $t("message.product_modal.general.qty_sold")
                      }}</span>
                      <span class="info-value"
                        >{{ productFull.part.qtySold }}
                        <span class="info-meta"
                          >({{ $t("message.product_modal.general.unificado") }}
                          {{ productFull.part.qtySoldUnificado }})</span
                        ></span
                      >
                    </div>
                  </template>
                </div>
                <hr />
                <div class="info-field" v-if="productFull.locations">
                  <span class="info-label">{{
                    $t("message.product_modal.general.location")
                  }}</span>
                  <span class="info-value">{{ productFull.locations }}</span>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.list_price")
                  }}</span>
                  <span class="info-value">{{
                    productFull.part.listPrice | currencyFormatted(true)
                  }}</span>
                </div>
                <div class="info-field" v-if="userIsSuperadmin">
                  <span class="info-label">Cost</span>
                  <span class="info-value">{{
                    productFull.part.cost | currencyFormatted(true)
                  }}</span>
                </div>
                <div class="info-field" v-if="userIsSuperadmin">
                  <span class="info-label">Costo referencial</span>
                  <span class="info-value">{{
                    productFull.part.costoReferencial | currencyFormatted(true)
                  }}</span>
                </div>
                <div v-if="productFull.part.pack === 'KIT'">
                  <div>
                    <p>
                      {{
                        $t(
                          "message.product_modal.general.kit_no_discount_price"
                        )
                      }}:
                      {{
                        productFull.part.kitNoDiscountPrice
                          | currencyFormatted(true)
                      }}
                    </p>
                    <p>
                      {{ $t("message.product_modal.general.kit_price") }}:
                      {{ productFull.part.kitPrice | currencyFormatted(true) }}
                    </p>
                    <p>
                      {{ $t("message.product_modal.general.kit_stock") }}:
                      {{ productFull.part.kitStock }}
                    </p>
                  </div>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.core")
                  }}</span>
                  <span
                    class="info-value font-bold"
                    :class="{ 'text-red-500': productFull.part.core > 0 }"
                  >
                    {{ productFull.part.core | currencyFormatted(true) }}
                  </span>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.dependencia")
                  }}</span>
                  <span class="info-value">{{
                    productFull.part.dependencia
                  }}</span>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.codigo_subtipo")
                  }}</span>
                  <span class="info-value">{{
                    productFull.part.codigoSubtipo
                  }}</span>
                </div>

                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.can_provide")
                  }}</span>
                  <span class="info-value font-bold">{{
                    productFull.part.canProvide
                  }}</span>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.incoming_pieces")
                  }}</span>
                  <span class="info-value font-bold">{{
                    productFull.part.incomingPieces
                  }}</span>
                </div>
                <template v-if="!editPart">
                  <div class="info-field">
                    <span class="info-label">{{
                      $t("message.product_modal.general.google_ads")
                    }}</span>
                    <span class="info-value">{{
                      productFull.part.googleAds
                    }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="flex flex-col">
                    <label for="google-ads-selector">{{
                      $t(
                        "message.product_modal.general.google_ads_category_assigned"
                      )
                    }}</label>
                    <select
                      name="google-ads-selector"
                      id="google-ads-selector"
                      class="default-select full bordered"
                      v-model="productFull.part.googleAds"
                    >
                      <option
                        v-for="googleAdsCategory in getGoogleAdsCategories"
                        :key="googleAdsCategory.id"
                        :value="googleAdsCategory.code"
                      >
                        {{ googleAdsCategory.name }}
                      </option>
                    </select>
                  </div>
                </template>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.brand")
                  }}</span>
                  <span class="info-value">{{ productFull.part.brand }}</span>
                </div>
                <template v-if="!editPart">
                  <div class="info-field">
                    <span class="info-label">{{
                      $t("message.product_modal.general.average_shipping_price")
                    }}</span>
                    <span class="info-value text-blue-600 font-bold"
                      >${{ productFull.part.averageShippingPrice }}</span
                    >
                  </div>
                </template>
                <template v-else>
                  <div class="flex flex-col">
                    <label for="avg_shipping_price"
                      >{{
                        $t(
                          "message.product_modal.general.average_shipping_price"
                        )
                      }}
                      $</label
                    >
                    <input
                      type="number"
                      step="0.01"
                      id="avg_shipping_price"
                      inputmode="decimal"
                      name="avg_shipping_price"
                      class="regular full bordered"
                      v-model="productFull.part.averageShippingPrice"
                    />
                  </div>
                </template>
                <template v-if="!editPart">
                  <div class="info-field">
                    <span class="info-label">{{
                      $t("message.product_modal.general.weight")
                    }}</span>
                    <span class="info-value">{{
                      productFull.part.weight
                    }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="flex flex-col">
                    <label for="weight">{{
                      $t("message.product_modal.general.weight")
                    }}</label>
                    <input
                      type="number"
                      step="0.01"
                      id="weight"
                      inputmode="decimal"
                      name="weight"
                      class="regular full bordered"
                      v-model="productFull.part.weight"
                    />
                  </div>
                </template>
                <div class="info-field">
                  <span class="info-label">{{
                    $t("message.product_modal.general.quantity_min_sale")
                  }}</span>
                  <span class="info-value">{{
                    productFull.part.quantityMinSale
                  }}</span>
                </div>
                <div class="info-field">
                  <span class="info-label">{{
                    $t(
                      "message.product_modal.general.quantity_min_sale_dropship"
                    )
                  }}</span>
                  <span class="info-value">{{
                    productFull.part.basePackageQtyDropship
                  }}</span>
                </div>

                <template v-if="!editPart">
                  <div class="info-field">
                    <span class="info-label">{{
                      $t("message.product_modal.general.dimensions")
                    }}</span>
                    <span class="info-value"
                      >{{ productFull.part.length }} ×
                      {{ productFull.part.height }} ×
                      {{ productFull.part.width }}</span
                    >
                  </div>
                </template>
                <template v-else>
                  <div class="grid grid-cols-3 gap-2">
                    <div class="min-w-0">
                      <label for="length">{{
                        $t("message.product_modal.general.length")
                      }}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="length"
                        inputmode="decimal"
                        name="length"
                        class="regular full bordered"
                        v-model="productFull.part.length"
                      />
                    </div>
                    <div class="min-w-0">
                      <label for="height">{{
                        $t("message.product_modal.general.height")
                      }}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="height"
                        inputmode="decimal"
                        name="height"
                        class="regular full bordered"
                        v-model="productFull.part.height"
                      />
                    </div>
                    <div class="min-w-0">
                      <label for="width">{{
                        $t("message.product_modal.general.width")
                      }}</label>
                      <input
                        type="number"
                        step="0.01"
                        id="width"
                        inputmode="decimal"
                        name="width"
                        class="regular full bordered"
                        v-model="productFull.part.width"
                      />
                    </div>
                  </div>
                </template>

                <!-- Shipping Configuration Section -->
                <template v-if="editPart">
                  <div class="shipping-config is-editing">
                    <h4 class="shipping-config-title">
                      {{
                        $t(
                          "message.product_modal.general.shipping_configuration"
                        )
                      }}
                    </h4>

                    <div class="flex flex-col gap-3">
                      <!-- Individual Box Required -->
                      <div class="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="shipping_individual_box_required"
                          v-model="
                            productFull.part.shippingIndividualBoxRequired
                          "
                          true-value="1"
                          false-value="0"
                          class="w-5 h-5 mt-0.5 flex-shrink-0"
                        />
                        <label
                          for="shipping_individual_box_required"
                          class="cursor-pointer"
                        >
                          <strong>{{
                            $t(
                              "message.product_modal.general.requires_individual_box"
                            )
                          }}</strong>
                          -
                          {{
                            $t(
                              "message.product_modal.general.requires_individual_box_desc"
                            )
                          }}
                        </label>
                      </div>

                      <!-- Package With Similar Only -->
                      <div class="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="shipping_can_package_with_similar_only"
                          v-model="
                            productFull.part.shippingCanPackageWithSimilarOnly
                          "
                          true-value="1"
                          false-value="0"
                          class="w-5 h-5 mt-0.5 flex-shrink-0"
                        />
                        <label
                          for="shipping_can_package_with_similar_only"
                          class="cursor-pointer"
                        >
                          <strong>{{
                            $t(
                              "message.product_modal.general.package_with_similar_only"
                            )
                          }}</strong>
                          -
                          {{
                            $t(
                              "message.product_modal.general.package_with_similar_only_desc"
                            )
                          }}
                        </label>
                      </div>

                      <!-- Requires Freight Shipping -->
                      <div class="flex items-start gap-2">
                        <input
                          type="checkbox"
                          id="requires_freight_shipping"
                          v-model="productFull.part.requiresFreightShipping"
                          true-value="1"
                          false-value="0"
                          class="w-5 h-5 mt-0.5 flex-shrink-0"
                        />
                        <label
                          for="requires_freight_shipping"
                          class="cursor-pointer"
                        >
                          <strong class="text-red-600">{{
                            $t(
                              "message.product_modal.general.requires_freight_shipping"
                            )
                          }}</strong>
                          -
                          {{
                            $t(
                              "message.product_modal.general.requires_freight_shipping_desc"
                            )
                          }}
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <!-- Display mode for shipping configuration -->
                  <div class="shipping-config">
                    <h4 class="shipping-config-title">
                      {{
                        $t(
                          "message.product_modal.general.shipping_configuration"
                        )
                      }}
                    </h4>
                    <div class="flex flex-col gap-1 text-sm">
                      <div
                        v-if="
                          productFull.part.shippingIndividualBoxRequired == 1 ||
                            productFull.part.shippingIndividualBoxRequired ===
                              true
                        "
                        class="text-orange-600"
                      >
                        ✓
                        {{
                          $t(
                            "message.product_modal.general.requires_individual_box"
                          )
                        }}
                      </div>
                      <div
                        v-if="
                          productFull.part.shippingCanPackageWithSimilarOnly ==
                            1 ||
                            productFull.part
                              .shippingCanPackageWithSimilarOnly === true
                        "
                        class="text-blue-600"
                      >
                        ✓
                        {{
                          $t(
                            "message.product_modal.general.package_with_similar_only"
                          )
                        }}
                      </div>
                      <div
                        v-if="
                          productFull.part.requiresFreightShipping == 1 ||
                            productFull.part.requiresFreightShipping === true
                        "
                        class="text-red-600 font-semibold"
                      >
                        ⚠
                        {{
                          $t(
                            "message.product_modal.general.requires_freight_shipping"
                          )
                        }}
                      </div>
                      <div
                        v-if="
                          productFull.part.shippingIndividualBoxRequired != 1 &&
                            productFull.part.shippingIndividualBoxRequired !==
                              true &&
                            productFull.part
                              .shippingCanPackageWithSimilarOnly != 1 &&
                            productFull.part
                              .shippingCanPackageWithSimilarOnly !== true &&
                            productFull.part.requiresFreightShipping != 1 &&
                            productFull.part.requiresFreightShipping !== true
                        "
                        class="text-gray-400"
                      >
                        {{
                          $t(
                            "message.product_modal.general.no_special_shipping_requirements"
                          )
                        }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-if="!editPart">
                  <div
                    class="info-field"
                    v-if="productFull.part.ebayCategoryId"
                  >
                    <span class="info-label">{{
                      $t("message.product_modal.general.ebay_category_id_desc")
                    }}</span>
                    <span class="info-value">{{
                      productFull.part.ebayCategoryId
                    }}</span>
                  </div>
                  <div
                    class="info-field"
                    v-if="productFull.part.amazonCategory"
                  >
                    <span class="info-label">{{
                      $t(
                        "message.product_modal.general.amazon_category_id_desc"
                      )
                    }}</span>
                    <span class="info-value">{{
                      productFull.part.amazonCategory
                    }}</span>
                  </div>
                </template>
                <template v-else>
                  <div>
                    <label>{{
                      $t("message.product_modal.general.ebay_category_id_desc")
                    }}</label>
                    <input
                      type="text"
                      name="ebay-category-id"
                      class="regular full bordered"
                      v-model="productFull.part.ebayCategoryId"
                    />
                  </div>
                  <div>
                    <label>{{
                      $t(
                        "message.product_modal.general.amazon_category_id_desc"
                      )
                    }}</label>
                    <input
                      type="text"
                      name="amazon-category-id"
                      class="regular full bordered"
                      v-model="productFull.part.amazonCategory"
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Barra fija al editar: el formulario es largo y en móvil los
               botones de arriba quedan fuera de vista -->
          <div v-if="editPart && userCanEditProducts" class="edit-bar">
            <span class="edit-bar-text">
              <i class="mdi mdi-pencil"></i>
              {{
                partIsModified
                  ? $t("message.product_modal.general.unsaved_changes")
                  : $t("message.product_modal.general.editing")
              }}
            </span>
            <div class="edit-bar-actions">
              <button type="button" class="pb-btn" @click="revertMainInfo()">
                {{ $t("message.product_modal.general.cancel") }}
              </button>
              <button
                type="button"
                class="pb-btn pb-btn-success"
                :disabled="!partIsModified"
                @click="saveMainInfo()"
              >
                <i class="mdi mdi-content-save-outline"></i>
                {{ $t("message.product_modal.general.save") }}
              </button>
            </div>
          </div>
        </template>
        <template v-if="selectedSection == 'attributes'">
          <!-- attributes -->
          <div class="box w-full">
            <div class="box-header">
              {{ $t("message.product_modal.tabs.attributes") }}
            </div>
            <div class="box-content-block">
              <ProductAttributesManager
                :code="code"
                :attributes="productFull.attributes"
                :optionsCount="productFull.similarParts.length"
                @newAttributeAdded="getProduct()"
              ></ProductAttributesManager>
            </div>
          </div>
        </template>
        <!-- images section -->
        <template v-if="selectedSection == 'images'">
          <!-- images -->
          <div class="box w-full">
            <div class="box-header">
              {{ $t("message.product_modal.tabs.images") }}
            </div>
            <div class="box-content-block">
              <ProductImagesManager
                @addImageToDescription="addImageToDescription"
                @imageUploaded="addedImage"
                @imageDeleted="deletedImage"
                @generateKitImage="generateKitImage"
                @imageSetMain="imageSetMain"
                @imagesUpdated="getProduct()"
                :code="code"
                :productType="productFull.part.pack"
                :images="productFull.images"
              />
            </div>
          </div>
        </template>
        <!-- applications-box -->
        <template v-if="selectedSection == 'applications'">
          <!-- applications -->
          <div class="box full">
            <VehicleApplicationManager :unified="productFull.part.unificado" />
          </div>
        </template>
        <!-- kit box -->
        <template v-if="selectedSection == 'kits'">
          <!-- kits -->
          <div class="box">
            <div class="box-header">
              {{ $t("message.product_modal.tabs.kits") }}
            </div>
            <div class="box-content-block">
              <KitsRelatedToProduct :code="code"></KitsRelatedToProduct>
            </div>
          </div>
        </template>
        <!-- marketplaces -->
        <template v-if="selectedSection == 'marketplaces'">
          <ProductMarketplacesManager
            v-if="productFull"
            :code="productFull.part.nkrCode"
          />
          <MarketplacesHtml v-if="productFull" :product="productFull" />
        </template>
        <!-- categories -->
        <template v-if="selectedSection == 'categories'">
          <!-- categories -->
          <div class="box mid">
            <div class="box-header">
              {{ $t("message.product_modal.tabs.categories") }}
            </div>
            <div class="box-content-block">
              <CategoryManager
                :unificado="productFull.part.unificado"
                :part="productFull.part"
              />
            </div>
          </div>
        </template>
        <!-- related products -->
        <template v-if="selectedSection == 'related_products'">
          <!-- products related -->
          <ProductsBaseRelatedManager :unificado="productFull.part.unificado" />
        </template>
        <!-- sold together products -->
        <template v-if="selectedSection == 'sold_together'">
          <!-- products related -->
          <ProductsSoldTogether :code="productFull.part.nkrCode" />
        </template>
        <!-- sync -->
        <template v-if="selectedSection == 'sync'">
          <!-- products related -->
          <ProductsSync
            :code="productFull.part.nkrCode"
            @partUpdated="getProduct"
          />
        </template>
        <template v-if="selectedSection == 'alerts'">
          <!-- products related -->
          <ProductsAlerts
            :code="productFull.part.nkrCode"
            @partUpdated="getProduct"
          />
        </template>
        <div v-if="selectedSection == 'kit_items'" class="w-full">
          <KitItemsManager
            :code="productFull.part.nkrCode"
            @kitUpdated="getProduct"
          />
        </div>
        <!-- supersession -->
        <template v-if="selectedSection == 'supersession'">
          <!-- Supersession -->
          <div class="box full">
            <div class="box-header">
              {{ $t("message.product_modal.tabs.supersession") }}
            </div>
            <div class="box-content-block">
              <SupersessionManager :unificado="productFull.part.unificado" />
            </div>
          </div>
        </template>
        <!-- strong related -->
        <template v-if="selectedSection == 'products_variations'">
          <!-- products strong related -->
          <ProductsVariations :unificado="productFull.part.unificado" />
        </template>
        <!-- stats -->
        <template v-if="selectedSection == 'stats'">
          <div class="stats-wrap">
            <div v-if="statsLoading" class="stats-loading">
              <BaseSmallSpinner />
            </div>
            <template v-else-if="statsData">
              <div class="stats-cards">
                <div class="stat-card green">
                  <div class="stat-label">Overall Revenue</div>
                  <div class="stat-value">
                    {{
                      statsData.summary.overallRevenue | currencyFormatted(true)
                    }}
                  </div>
                </div>
                <div class="stat-card blue">
                  <div class="stat-label">Overall Units Sold</div>
                  <div class="stat-value">
                    {{ statsData.summary.overallUnitsSold }}
                  </div>
                </div>
                <div class="stat-card blue">
                  <div class="stat-label">Overall Orders</div>
                  <div class="stat-value">
                    {{ statsData.summary.overallOrders }}
                  </div>
                </div>
                <div class="stat-card green">
                  <div class="stat-label">This Year Revenue</div>
                  <div class="stat-value">
                    {{
                      statsData.summary.thisYearRevenue
                        | currencyFormatted(true)
                    }}
                  </div>
                </div>
                <div class="stat-card blue">
                  <div class="stat-label">This Year Units</div>
                  <div class="stat-value">
                    {{ statsData.summary.thisYearUnitsSold }}
                  </div>
                </div>
                <div class="stat-card blue">
                  <div class="stat-label">This Year Orders</div>
                  <div class="stat-value">
                    {{ statsData.summary.thisYearOrders }}
                  </div>
                </div>
                <div class="stat-card gray">
                  <div class="stat-label">Last Year Revenue</div>
                  <div class="stat-value">
                    {{
                      statsData.summary.lastYearRevenue
                        | currencyFormatted(true)
                    }}
                  </div>
                </div>
                <div class="stat-card gray">
                  <div class="stat-label">Last Year Units</div>
                  <div class="stat-value">
                    {{ statsData.summary.lastYearUnitsSold }}
                  </div>
                </div>
                <div class="stat-card gray">
                  <div class="stat-label">Last Year Orders</div>
                  <div class="stat-value">
                    {{ statsData.summary.lastYearOrders }}
                  </div>
                </div>
                <div class="stat-card red">
                  <div class="stat-label">Returns Amount</div>
                  <div class="stat-value">
                    {{
                      statsData.summary.overallReturnAmount
                        | currencyFormatted(true)
                    }}
                  </div>
                </div>
                <div class="stat-card red">
                  <div class="stat-label">Units Returned</div>
                  <div class="stat-value">
                    {{ statsData.summary.overallUnitsReturned }}
                  </div>
                </div>
                <div class="stat-card red">
                  <div class="stat-label">Return Count</div>
                  <div class="stat-value">
                    {{ statsData.summary.overallReturnCount }}
                  </div>
                </div>
              </div>
              <div
                class="stats-table-wrap"
                v-if="statsData.yearlyRows && statsData.yearlyRows.length"
              >
                <table class="stats-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Orders</th>
                      <th>Units Sold</th>
                      <th>Revenue</th>
                      <th>Returns</th>
                      <th>Units Returned</th>
                      <th>Return Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in statsData.yearlyRows"
                      :key="row.year"
                      :class="{ 'current-year': row.year == statsCurrentYear }"
                    >
                      <td>{{ row.year }}</td>
                      <td>{{ row.orderCount }}</td>
                      <td>{{ row.unitsSold }}</td>
                      <td>{{ row.revenue | currencyFormatted(true) }}</td>
                      <td>{{ row.returnCount }}</td>
                      <td>{{ row.unitsReturned }}</td>
                      <td>{{ row.returnAmount | currencyFormatted(true) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            <div v-else class="stats-empty">No stats available.</div>
          </div>
        </template>
        <!-- orders containing the product -->
        <template v-if="selectedSection == 'orders'">
          <div class="stats-wrap">
            <div
              v-if="productOrdersLoading && !productOrders.length"
              class="stats-loading"
            >
              <BaseSmallSpinner />
            </div>
            <div v-else-if="!productOrders.length" class="stats-empty">
              {{ $t("message.product_modal.orders.empty") }}
            </div>
            <template v-else>
              <p class="orders-count">
                {{
                  $t("message.product_modal.orders.showing", {
                    shown: productOrders.length,
                    total: productOrdersTotal
                  })
                }}
              </p>
              <div class="stats-table-wrap">
                <table class="stats-table product-orders-table">
                  <thead>
                    <tr>
                      <th>{{ $t("message.product_modal.orders.order") }}</th>
                      <th>{{ $t("message.product_modal.orders.date") }}</th>
                      <th>{{ $t("message.product_modal.orders.customer") }}</th>
                      <th>{{ $t("message.product_modal.orders.seller") }}</th>
                      <th>{{ $t("message.product_modal.orders.status") }}</th>
                      <th>{{ $t("message.product_modal.orders.payment") }}</th>
                      <th class="text-right">
                        {{ $t("message.product_modal.orders.qty") }}
                      </th>
                      <th class="text-right">
                        {{ $t("message.product_modal.orders.product_amount") }}
                      </th>
                      <th class="text-right">
                        {{ $t("message.product_modal.orders.order_total") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="order in productOrders"
                      :key="order.orderNumber"
                      @click="openOrder(order.orderNumber)"
                    >
                      <td class="order-link">{{ order.orderNumber }}</td>
                      <td>{{ order.orderDate | dateWithHours }}</td>
                      <td>{{ order.shippingAddressName || "-" }}</td>
                      <td>
                        {{
                          order.sellerName || order.salesman || order.createdBy
                        }}
                      </td>
                      <td>
                        <span
                          class="order-status"
                          :class="orderStatusTone(order.status)"
                        >
                          {{ orderStatusLabel(order.status) }}
                        </span>
                      </td>
                      <td
                        :class="
                          order.statusPayment == 1
                            ? 'text-green-600'
                            : 'text-red-500'
                        "
                      >
                        {{
                          order.statusPayment == 1
                            ? $t("message.product_modal.orders.paid")
                            : $t("message.product_modal.orders.pending")
                        }}
                      </td>
                      <td class="text-right">{{ order.productQuantity }}</td>
                      <td class="text-right">
                        {{ order.productAmount | currencyFormatted(true) }}
                      </td>
                      <td class="text-right">
                        {{ order.total | currencyFormatted(true) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                v-if="productOrders.length < productOrdersTotal"
                class="orders-more"
              >
                <button
                  type="button"
                  class="pb-btn"
                  :disabled="productOrdersLoading"
                  @click="loadProductOrders()"
                >
                  <span v-if="!productOrdersLoading">
                    {{ $t("message.product_modal.orders.load_more") }}
                  </span>
                  <span v-else>
                    {{ $t("message.product_modal.orders.loading") }}
                  </span>
                </button>
              </div>
            </template>
          </div>
        </template>
      </div>
    </div>
    <div v-else class="loading-full overflow-hidden">
      <BaseSmallSpinner />
    </div>

    <AiSuggestionPanel
      :is-open="showAiPanel"
      :fetch-suggestions="fetchProductSuggestions"
      context-label="product"
      :editor-chips="[
        'Improve titles',
        'Suggest keywords',
        'Optimize for SEO',
        'Review all fields'
      ]"
      :info-chips="[
        'What vehicles is this compatible with?',
        'How many needed per vehicle?',
        'What does this part do?'
      ]"
      @close="showAiPanel = false"
      @apply="applyAiChanges"
    />
  </BaseModal>
</template>
<script>
import ProductsVariations from "@/components/product/ProductsVariationsManager.vue";
import ProductsSoldTogether from "@/components/product/ProductsSoldTogether.vue";
import ProductsBaseRelatedManager from "@/components/product/ProductsBaseRelatedManager.vue";
import ProductImagesManager from "@/components/product/ProductImagesManager.vue";
import ProductsSync from "@/components/product/ProductsSync.vue";
import ProductsAlerts from "@/components/product/ProductsAlerts.vue";
import KitItemsManager from "@/components/product/KitItemsManager.vue";
import VehicleApplicationManager from "@/components/product/VehicleApplicationManager.vue";
import KitsRelatedToProduct from "@/components/product/KitsRelatedToProduct.vue";
import {
  getProduct,
  changeProductStatus as changeProductStatusStore,
  updateProduct as updateProductStore,
  deleteLocalProduct
} from "@/api/store/ProductsService.js";
import { saveCatalogPrice } from "@/api/store/CatalogPricingService.js";
import { loadPriceGroups } from "@/api/store/PriceGroupsService.js";
import { getSettings } from "@/api/store/SettingsService.js";
import { DEFAULT_DEALER_PROFIT } from "@/utils/priceGroupCalculator.js";
import CatalogPriceForm, {
  emptyCatalogDraft
} from "@/components/product/CatalogPriceForm.vue";
import SupersessionManager from "@/components/product/SupersessionManager.vue";
import CategoryManager from "@/components/product/CategoryManager.vue";
import ProductAttributesManager from "@/components/product/ProductAttributesManager.vue";
import ProductMarketplacesManager from "@/components/product/ProductMarketplacesManager.vue";
import { mapGetters } from "vuex";
import MarketplacesHtml from "@/components/product/MarketplacesHtml.vue";
import { VueEditor } from "vue2-editor";
import { generateKitImage as generateKitImageApi } from "@/api/masterAdmin/ProductImagesService.js";
import { loadKitItems as loadKitItemsApi } from "@/api/store/ProductsService.js";
import ProductStockAndPrice from "@/components/part/ProductStockAndPrice.vue";
import ReportLostSaleModal from "@/components/ReportLostSaleModal.vue";
import { getProductStats } from "@/api/store/Statistics.js";
import { getOrdersByProduct } from "@/api/store/OrdersService.js";
import { getOrderStatusKey } from "@/constants/orderStatusMap";
import AiSuggestionPanel from "@/components/ai/AiSuggestionPanel.vue";
import { suggestProduct, askProduct } from "@/api/store/AiService.js";
import { getApplications } from "@/api/store/ApplicationsService.js";
export default {
  components: {
    VueEditor,
    ProductsSync,
    ProductsAlerts,
    ProductStockAndPrice,
    CatalogPriceForm,
    KitItemsManager,
    KitsRelatedToProduct,
    MarketplacesHtml,
    ProductImagesManager,
    VehicleApplicationManager,
    CategoryManager,
    ProductAttributesManager,
    SupersessionManager,
    ProductsVariations,
    ProductsBaseRelatedManager,
    ProductsSoldTogether,
    ProductMarketplacesManager,
    ReportLostSaleModal,
    AiSuggestionPanel
  },
  props: {
    code: {
      type: String,
      required: true
    },
    value: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      tabsCanScrollLeft: false,
      tabsCanScrollRight: false,
      productFull: null,
      notFound: false,
      deletingOrphan: false,
      catalogDraft: emptyCatalogDraft(),
      priceGroups: [],
      dealerProfit: DEFAULT_DEALER_PROFIT,
      savingCatalog: false,
      processing: false,
      titleUpdateSimilar: false,
      titleStdUpdateSimilar: false,
      titleAltUpdateSimilar: false,
      isOpenModal: this.value,
      keywordsOptions: [],
      editPart: false,
      productMainInfoOriginal: null,
      showMenu: false,
      selectedSection: "general",
      showSimilarParts: false,
      unificadoUpdateSimilar: false,
      showRestockModal: false,
      statsData: null,
      statsLoading: false,
      productOrders: [],
      productOrdersTotal: 0,
      productOrdersPage: 0,
      productOrdersLoading: false,
      showAiPanel: false,
      aiApplications: []
    };
  },
  computed: {
    ...mapGetters("general", ["getGoogleAdsCategories", "getActiveStore"]),
    currentDepartment() {
      return this.$store.getters["general/getActiveDepartment"]?.code;
    },
    labelSizes() {
      return [
        { code: "XS", title: "2×1 in" },
        { code: "S", title: "3×2 in" },
        { code: "M", title: "4×3 in" },
        { code: "L", title: "6×4 in" }
      ];
    },
    userCanSeePrecio4() {
      return this.$store.getters["auth/userHasPermission"]("price_precio4");
    },
    userCanSeePrecio5() {
      return this.$store.getters["auth/userHasPermission"]("price_precio5");
    },
    userCanSeePrecio6() {
      return this.$store.getters["auth/userHasPermission"]("price_precio6");
    },
    userCanEditProducts() {
      return this.$store.getters["auth/userHasPermission"]("can_edit_products");
    },
    userCanUseAIFeatures() {
      return this.$store.getters["auth/userHasPermission"](
        "can_use_ia_features"
      );
    },
    userIsSuperadmin() {
      return this.$store.getters["auth/currentUser"]?.role === "superadmin";
    },
    userCanViewProductHistory() {
      return this.$store.getters["auth/userHasPermission"](
        "view_product_history"
      );
    },
    isEnabled() {
      if (
        this.productFull?.part.enabled === "false" ||
        this.productFull?.part.enabled === false
      ) {
        return false;
      } else {
        return true;
      }
    },
    productTabs() {
      if (!this.productFull) return [];
      const isKit = this.productFull.part.pack === "KIT";
      const tabs = [
        { key: "general", icon: "mdi-information-outline" },
        { key: "stats", icon: "mdi-chart-line" },
        { key: "images", icon: "mdi-image-multiple-outline" },
        { key: "attributes", icon: "mdi-tune-variant" },
        isKit && { key: "kit_items", icon: "mdi-package-variant" },
        { key: "applications", icon: "mdi-car-outline" },
        !isKit && { key: "kits", icon: "mdi-package-variant-closed" },
        {
          key: "marketplaces",
          label: "marketplace",
          icon: "mdi-storefront-outline"
        },
        { key: "categories", icon: "mdi-shape-outline" },
        { key: "supersession", icon: "mdi-swap-horizontal" },
        { key: "products_variations", icon: "mdi-source-branch" },
        { key: "related_products", icon: "mdi-link-variant" },
        { key: "sold_together", icon: "mdi-cart-plus" },
        { key: "sync", icon: "mdi-sync" },
        { key: "alerts", icon: "mdi-bell-outline" },
        this.userCanViewProductHistory && { key: "orders", icon: "mdi-receipt" }
      ];
      return tabs.filter(Boolean).map(tab => ({
        ...tab,
        label: this.$t(`message.product_modal.tabs.${tab.label || tab.key}`)
      }));
    },
    isCatalogDependencia() {
      const dep = this.productFull?.part?.dependencia;
      return dep === "B2" || dep === "Z";
    },
    isOpen() {
      return this.value;
    },
    keywordsDestructured() {
      if (
        this.productFull?.part?.keywords == "" ||
        !this.productFull?.part?.keywords
      ) {
        return [];
      }
      return this.productFull?.part?.keywords?.split(",");
    },
    partIsModified() {
      if (!this.productMainInfoOriginal) {
        return false;
      }
      return (
        this.productMainInfoOriginal != JSON.stringify(this.productFull.part)
      );
    },
    similarParts() {
      return this.productFull.similarParts;
    },
    totalStock() {
      if (!this.productFull?.part?.stockAndPrice) {
        return 0;
      }
      return this.productFull.part.stockAndPrice.reduce(
        (total, entry) => total + (entry.stock || 0),
        0
      );
    },
    activeDepartmentId() {
      return this.$store.getters["general/getActiveDepartment"]?.full || "";
    },
    statsCurrentYear() {
      return new Date().getFullYear();
    }
  },
  watch: {
    isOpenModal(val) {
      this.$emit("input", val);
    },
    productTabs() {
      this.$nextTick(this.updateTabsScroll);
    },
    editPart(val) {
      // Al salir de edición sin guardar, restaurar el precio de catálogo persistido.
      if (!val && this.isCatalogDependencia && this.productFull) {
        this.setupCatalogPricing(this.productFull);
      }
    },
    selectedSection(val) {
      this.showMenu = false;
      this.scrollActiveTabIntoView();
      if (val === "stats" && !this.statsData) {
        this.loadStats();
      }
      if (val === "orders" && !this.productOrdersPage) {
        this.loadProductOrders();
      }
    },
    showAiPanel(val) {
      if (
        val &&
        this.aiApplications.length === 0 &&
        this.productFull?.part?.unificado
      ) {
        getApplications(this.productFull.part.unificado)
          .then(res => {
            // getApplications returns {verification, applications: [...]}
            this.aiApplications = res.applications || res || [];
          })
          .catch(() => {});
      }
    },
    value(val) {
      if (val) {
        // Check if there's a tab in the URL, otherwise default to general
        const tabParam = this.$route.query.tab;
        this.selectedSection = tabParam || "general";
        this.productFull = null;
        this.notFound = false;
        this.statsData = null;
        this.productOrders = [];
        this.productOrdersTotal = 0;
        this.productOrdersPage = 0;
        this.getProduct();
        const query = { ...this.$route.query, productOpen: this.code };
        this.$router.replace({ query }).catch(() => {});
      } else {
        const query = { ...this.$route.query };
        delete query.productOpen;
        this.$router.replace({ query }).catch(() => {});
        this.showAiPanel = false;
        this.aiApplications = [];
      }
      this.isOpenModal = val;
    }
  },
  methods: {
    async fetchProductSuggestions(prompt, history, mode) {
      if (mode === "info") {
        return askProduct(
          prompt,
          this.productFull.part,
          this.aiApplications,
          history
        );
      }
      return suggestProduct(
        prompt,
        this.productFull.part,
        this.aiApplications,
        history
      );
    },

    applyAiChanges(changes) {
      changes.forEach(c => {
        this.$set(this.productFull.part, c.field, c.value);
      });
      this.editPart = true;
      this.$toast.open({
        message: `${changes.length} AI suggestion${
          changes.length !== 1 ? "s" : ""
        } applied — review and save`,
        type: "success",
        position: "top-right"
      });
    },

    handleRestockSuccess(data) {
      this.$toast.open({
        message: "Restock request submitted successfully!",
        type: "success",
        position: "top-right"
      });
      console.log("Restock request created:", data);
    },
    // Generador de etiquetas v2 (/labels) con la pieza y el tamaño
    // preseleccionados; sin tamaño usa el sugerido por la caja
    openLabelGenerator(size) {
      const query = new URLSearchParams({
        partNumber: this.productFull.part.nkrCode
      });
      if (size) query.set("size", size);
      if (this.currentDepartment) {
        query.set("departmentCode", this.currentDepartment);
      }
      window
        .open(`${this.getActiveStore.endpoint}/labels?${query}`, "_blank")
        .focus();
    },
    addKeyword() {
      let elem = document.getElementById("keyword-input");
      if (elem.value == "") {
        return;
      }
      let actualKeywords = [...this.keywordsDestructured];
      if (
        actualKeywords.find(x => {
          return x == elem.value;
        })
      ) {
        alert(`${elem.value} already exist`);
        return;
      }
      actualKeywords.push(elem.value);
      this.productFull.part.keywords = actualKeywords.join(",");
      elem.value = "";
    },
    removeKeyword(keywordToRemove) {
      let actualKeywords = [...this.keywordsDestructured];
      let keywordPosition = actualKeywords.findIndex(x => {
        return x == keywordToRemove;
      });
      console.log(keywordPosition);
      actualKeywords.splice(keywordPosition, 1);
      console.log(actualKeywords);
      this.productFull.part.keywords = actualKeywords.join(",");
    },
    async getProduct() {
      const result = await getProduct(this.code);
      // El backend devuelve false cuando el producto no está en el catálogo
      // maestro (part_info_original), típicamente un huérfano local.
      if (!result || !result.part) {
        this.notFound = true;
        this.productFull = null;
        return;
      }
      this.notFound = false;
      this.productFull = result;
      this.productMainInfoOriginal = JSON.stringify(this.productFull.part);
      this.setupCatalogPricing(result);
    },
    // Para B2/Z prepara el editor de precio de catálogo con la fila existente
    // (o defaults) y carga los price groups para el select.
    setupCatalogPricing(result) {
      if (!this.isCatalogDependencia) {
        return;
      }
      if (!this.priceGroups.length) {
        loadPriceGroups()
          .then(res => {
            this.priceGroups = res;
          })
          .catch(() => {});
      }
      getSettings("lp45_dealer_profit").then(res => {
        if (res && res.value !== undefined && res.value !== null) {
          this.dealerProfit = Number(res.value);
        }
      });
      const cp = result.catalogPrice;
      const base = emptyCatalogDraft();
      if (cp) {
        Object.keys(base).forEach(key => {
          if (cp[key] !== undefined && cp[key] !== null) {
            base[key] =
              typeof base[key] === "number" && key !== "priceLogic"
                ? Number(cp[key])
                : cp[key];
          }
        });
      }
      this.catalogDraft = base;
    },
    async saveCatalogDraft() {
      this.savingCatalog = true;
      try {
        await saveCatalogPrice({
          nkrCode: this.productFull.part.nkrCode,
          ...this.catalogDraft
        });
        this.$toast.open({
          message: "Catalog price saved",
          type: "success",
          position: "top-right"
        });
        this.getProduct();
      } catch (e) {
        const message =
          e.response?.data ||
          e.response?.data?.error?.message ||
          "Error saving catalog price";
        this.$toast.open({
          message: typeof message === "string" ? message : "Error saving",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.savingCatalog = false;
      }
    },
    async deleteOrphanProduct() {
      if (
        !confirm(
          `Delete orphaned local product ${this.code}? This removes it from part_info_local (it is not in the master catalog).`
        )
      ) {
        return;
      }
      this.deletingOrphan = true;
      try {
        await deleteLocalProduct(this.code);
        this.$toast.open({
          message: `Product ${this.code} deleted.`,
          type: "success",
          position: "top-right"
        });
        this.$emit("productDeleted", this.code);
        this.isOpenModal = false;
        this.$emit("input", false);
      } catch (e) {
        const message =
          e.response?.data?.error?.message || "Error deleting product.";
        this.$toast.open({
          message,
          type: "error",
          position: "top-right"
        });
      } finally {
        this.deletingOrphan = false;
      }
    },
    updateProductStatus(code, enabled) {
      const message = enabled
        ? "do you want to enable this part?"
        : "do you want to disable this part?";
      if (confirm(message)) {
        changeProductStatusStore(code, enabled)
          .then(() => {
            this.$emit("productUpdated", this.productFull.part);
            this.productFull.part.enabled = enabled;
            this.$toast.open({
              message: "status updated",
              type: "success",
              position: "top-right"
            });
          })
          .catch(e => {
            let message = e.response.data?.message || "Error updating status";
            this.$toast.open({
              message: message,
              type: "error",
              position: "top-right"
            });
          });
      }
    },
    revertMainInfo() {
      this.productFull.part = JSON.parse(this.productMainInfoOriginal);
      this.editPart = false;
    },
    saveMainInfo() {
      this.editPart = false;
      const newPart = {
        titleUpdateSimilar: this.titleUpdateSimilar,
        titleStdUpdateSimilar: this.titleStdUpdateSimilar,
        titleAltUpdateSimilar: this.titleAltUpdateSimilar,
        unificadoUpdateSimilar: this.unificadoUpdateSimilar,
        ...this.productFull.part
      };
      updateProductStore(newPart)
        .then(() => {
          this.getProduct();
          this.$toast.open({
            message: "Part updated",
            type: "success",
            position: "top-right"
          });
        })
        .catch(e => {
          const status = e?.response?.status;
          this.$toast.open({
            message:
              status === 403
                ? this.$t("message.product_modal.no_permission")
                : this.$t("message.product_modal.save_error"),
            type: "error",
            position: "top-right"
          });
        });
    },
    addedImage(image) {
      this.productFull.images.unshift(image);
    },
    /**
     * Inserta una imagen generada con IA al final de la descripción larga y
     * deja el modal en modo edición sobre la pestaña general, para que se vea
     * en el editor: el cambio recién se persiste al guardar el producto.
     */
    addImageToDescription(url) {
      // Quill 2 sólo conserva alt/height/width en las imágenes: un style en el
      // <img> se pierde al reserializar, así que el tamaño va por atributo.
      // El centrado va con la clase de Quill y el style inline: la clase
      // sobrevive a la edición, el style sirve donde no se cargue el CSS de
      // Quill (por ejemplo la web pública).
      const img =
        `<p class="ql-align-center" style="text-align:center">` +
        `<img src="${url}" width="500" /></p>`;
      this.productFull.part.longDescription =
        (this.productFull.part.longDescription || "") + img;
      this.selectedSection = "general";
      this.editPart = true;
      this.$toast.open({
        message: this.$t("message.product_images.added_to_description"),
        type: "success",
        position: "top-right"
      });
    },
    deletedImage(image) {
      const imageIndex = this.productFull.images.findIndex(
        i => i.id == image.id
      );
      this.productFull.images.splice(imageIndex, 1);
    },
    imageSetMain(image) {
      this.productFull.images.forEach(element => {
        if (image.id == element.id) {
          element.main = "1";
        } else {
          element.main = "0";
        }
      });
    },
    async generateKitImage() {
      this.processing = true;
      const items = await loadKitItemsApi(this.productFull.part.nkrCode);
      console.log(items);
      if (items.length == 0) {
        this.$toast.open({
          message: "No items in kit",
          type: "error",
          position: "top-right"
        });
        this.processing = false;
        return;
      }
      const formatedItems = this.productFull.part.kitData.items.map(item => {
        return {
          code: item.nkrCode,
          quantity: item.quantity
        };
      });
      generateKitImageApi(this.productFull.part.nkrCode, formatedItems)
        .then(() => {
          this.getProduct();
          this.$toast.open({
            message: "Image generated",
            type: "success",
            position: "top-right"
          });
        })
        .catch(() => {
          this.$toast.open({
            message: "Error generating image",
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.processing = false;
        });
    },
    selectTab(tabName) {
      this.selectedSection = tabName;
      // Update URL with the selected tab
      this.$router
        .push({
          query: {
            ...this.$route.query,
            tab: tabName
          }
        })
        .catch(() => {
          // Ignore navigation errors
        });
    },
    // Similares: se abren en otra pestaña para no perder una edición en curso
    openProduct(code) {
      const { href } = this.$router.resolve({
        name: "ProductsSearchPage",
        query: { productOpen: code }
      });
      window.open(href, "_blank");
    },
    // En móvil la barra de pestañas se desplaza: mantener visible la activa
    scrollActiveTabIntoView() {
      this.$nextTick(() => {
        const active = this.$refs.tabsBar?.querySelector(".is-active");
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
        this.updateTabsScroll();
      });
    },
    // Las flechas sólo aparecen cuando quedan pestañas ocultas de ese lado
    updateTabsScroll() {
      const bar = this.$refs.tabsBar;
      if (!bar) {
        this.tabsCanScrollLeft = false;
        this.tabsCanScrollRight = false;
        return;
      }
      this.tabsCanScrollLeft = bar.scrollLeft > 1;
      this.tabsCanScrollRight =
        bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 1;
    },
    scrollTabs(direction) {
      const bar = this.$refs.tabsBar;
      if (!bar) return;
      bar.scrollBy({
        left: direction * Math.max(bar.clientWidth * 0.7, 120),
        behavior: "smooth"
      });
    },
    // Sin touch ni trackpad, la rueda vertical del mouse mueve las pestañas
    onTabsWheel(event) {
      const bar = this.$refs.tabsBar;
      if (!bar || bar.scrollWidth <= bar.clientWidth) return;
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      bar.scrollLeft += event.deltaY;
    },
    async loadStats() {
      if (!this.code) return;
      this.statsLoading = true;
      try {
        this.statsData = await getProductStats(this.code);
      } catch (e) {
        console.error("Error loading product stats:", e);
      } finally {
        this.statsLoading = false;
      }
    },
    // Órdenes con este producto, de a una página: "cargar más" agrega la siguiente
    async loadProductOrders() {
      if (!this.code || this.productOrdersLoading) return;
      const code = this.code;
      this.productOrdersLoading = true;
      try {
        const res = await getOrdersByProduct(code, this.productOrdersPage + 1);
        // Se cerró o cambió de producto mientras cargaba
        if (code !== this.code) return;
        this.productOrders = this.productOrders.concat(res.orders);
        this.productOrdersTotal = res.totalItems;
        this.productOrdersPage += 1;
      } catch (e) {
        console.error("Error loading product orders:", e);
      } finally {
        this.productOrdersLoading = false;
      }
    },
    orderStatusLabel(status) {
      const key = getOrderStatusKey(status);
      return key
        ? this.$t(`message.orders_page.order_statuses.${key}`)
        : status;
    },
    orderStatusTone(status) {
      if (status === "Cancelled") return "tone-red";
      if (status === "Draft") return "tone-gray";
      if (status === "Shipped" || status === "Delivered") return "tone-green";
      return "tone-blue";
    },
    // Igual que los similares: otra pestaña, para no perder una edición en curso
    openOrder(orderNumber) {
      const { href } = this.$router.resolve({
        name: "OrderPage",
        params: { orderNumber }
      });
      window.open(href, "_blank");
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast.open({
            message: "Copied!",
            type: "success",
            position: "top-right",
            duration: 1000
          });
        })
        .catch(() => {
          // fallback for older browsers
          const el = document.createElement("textarea");
          el.value = text;
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        });
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateTabsScroll);
  },
  mounted() {
    window.addEventListener("resize", this.updateTabsScroll);
    if (this.value) {
      this.getProduct();
      // Load tab from URL on mount if modal is already open
      const tabParam = this.$route.query.tab;
      if (tabParam) {
        this.selectedSection = tabParam;
      }
    }
  }
};
</script>
<style scoped>
@import "~quill/dist/quill.core.css";
@import "~quill/dist/quill.bubble.css";
@import "~quill/dist/quill.snow.css";
</style>
<style lang="scss">
@import "@/assets/style/css/generic-variables.scss";
.catalog-price-block {
  margin-top: 8px;
  .catalog-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding: 6px 10px;
    background: #fef9c3;
    border: 1px solid #fde68a;
    border-radius: 6px;
    font-size: 0.85rem;
    color: #713f12;
  }
  .catalog-table {
    background: #fffbeb;
    th {
      background: #fef08a;
      color: #713f12;
    }
    .catalog-min-row {
      color: #a16207;
      font-size: 0.85rem;
      td:first-child {
        font-style: italic;
      }
    }
  }
}
.loading-full {
  height: 100%;
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
#similar-products {
  @media screen and (max-width: 768px) {
    .box-content-block {
      padding: 12px;

      button,
      p {
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
        max-width: 100%;
      }

      button {
        text-align: left;
        display: block;
        width: 100%;
      }

      p {
        margin: 5px 0;
      }
    }
  }
}

.similar-products-table-wrapper {
  -webkit-overflow-scrolling: touch;

  @media screen and (max-width: 768px) {
    max-width: 100%;
    overflow-x: auto;
    margin-top: 8px;

    .similar-products-table {
      min-width: 800px;
      width: auto;
      display: table;
    }
  }
}

.tiny-table {
  width: 100%;
  table-layout: auto;

  tr {
    th {
      padding: 2px 4px;
      white-space: nowrap;
      font-size: 14px;
      font-weight: 500;
      min-width: 80px;
    }
    td {
      padding: 5px 5px;
      white-space: nowrap;
      text-align: center;
      min-width: 80px;
    }
  }
  tr:nth-child(2n) {
    background-color: #d4d4d5;
  }
  tr:nth-child(2n + 1) {
    background-color: #ffffff;
  }
  tr:nth-child(1) {
    background-color: black;
    color: white;
  }
}
// Mismo lenguaje visual que _base-order-modal.vue. No es scoped porque el
// header vive en BaseModal y las tarjetas en subcomponentes: todo va dentro de
// .product-modal. Los estados usan .is-active y no .active, que
// _base-modal.vue fuerza globalmente a display: block. Los selectores llevan
// .modal para ganarle a los de .order-modal cuando este modal se abre desde
// una orden.
$pb-border: #e5e7eb;
$pb-muted: #6b7280;
$pb-text: #111827;
$pb-canvas: #f3f4f6;
$pb-accent-soft: rgba(18, 110, 106, 0.08);
$pb-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);

.product-modal.modal {
  &:focus {
    outline: none;
  }

  &:not(.full-page) {
    padding: 12px;

    .modal-bg {
      height: 100%;
    }

    > .modal-content {
      width: 100%;
      max-width: none;
      height: 100%;
      max-height: none;
      margin: 0;
      border-radius: 12px;
      overflow-y: auto;
    }

    @media screen and (max-width: 767px) {
      padding: 0;

      > .modal-content {
        height: 100vh;
        height: 100dvh;
        border-radius: 0;
      }
    }
  }

  > .modal-content {
    background-color: $pb-canvas;
    overscroll-behavior: contain;

    > .header.hasinfo {
      display: block;
      min-height: 0;
      padding: 12px 20px 0;
      background: white;
      border-bottom: 1px solid $pb-border;
      box-shadow: $pb-shadow;

      .info {
        display: block;
        color: $pb-text;
      }

      @media (max-width: 767px) {
        padding: 8px 12px 0;
      }
    }

    > .header .close {
      top: 12px;
      right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border-radius: 10px;
      transform: none;
      font-size: 28px;
      font-weight: 400;
      line-height: 1;
      color: $pb-muted;

      &:hover {
        background: $pb-canvas;
        color: $pb-text;
      }

      @media (max-width: 767px) {
        top: 8px;
        right: 8px;
      }
    }
  }

  /* ---------- Header ---------- */
  .product-header-main {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    // Deja lugar a la X de cerrar
    padding-right: 48px;

    @media (max-width: 767px) {
      gap: 10px;
      padding-right: 44px;
    }
  }

  .product-thumb {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border: 1px solid $pb-border;
    border-radius: 10px;
    background: white;
    object-fit: contain;

    &.is-empty {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f9fafb;
      font-size: 22px;
      color: #9ca3af;
    }

    @media (max-width: 767px) {
      width: 44px;
      height: 44px;
    }
  }

  .product-header-text {
    flex: 1;
    min-width: 0;
  }

  .product-header-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 10px;
  }

  .product-code {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
    color: $pb-muted;

    span {
      font-weight: 700;
      color: $pb-text;
    }

    @media (max-width: 767px) {
      font-size: 17px;
    }
  }

  .product-title {
    margin: 2px 0 0;
    overflow: hidden;
    font-size: 14px;
    line-height: 1.35;
    color: #4b5563;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .brand-pill,
  .status-badge,
  .meta-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
  }

  .brand-pill {
    background: $pb-accent-soft;
    color: $link-color-1;
  }

  .meta-pill {
    border: 1px solid $pb-border;
    background: #f9fafb;
    color: #4b5563;
  }

  .status-badge {
    .status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
    }

    &.tone-green {
      background: #dcfce7;
      color: #166534;
    }

    &.tone-red {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  // Móvil angosto: se oculta la dependencia del header (sigue en la ficha)
  // para que la línea de badges no pase de 2 filas
  @media (max-width: 480px) {
    .product-header-line .meta-pill {
      display: none;
    }
  }

  /* ---------- Pestañas ---------- */
  .product-tabs-wrap {
    position: relative;
    margin: 6px -20px 0;

    @media (max-width: 767px) {
      margin: 6px -12px 0;
    }
  }

  .product-tabs {
    display: flex;
    gap: 0;
    padding: 0 8px;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    @media (max-width: 767px) {
      padding: 0 4px;
    }
  }

  // Flechas sobre los bordes de la barra, con un degradé que tapa la
  // pestaña cortada
  .tabs-scroll-btn {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    width: 40px;
    padding: 0;
    font-size: 22px;
    color: $pb-muted;
    cursor: pointer;

    i {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid $pb-border;
      border-radius: 50%;
      background: white;
      box-shadow: $pb-shadow;
    }

    &:hover {
      color: $link-color-1;

      i {
        border-color: $link-color-1;
      }
    }

    &.is-left {
      left: 0;
      justify-content: flex-start;
      padding-left: 4px;
      background: linear-gradient(to right, white 55%, rgba(255, 255, 255, 0));
    }

    &.is-right {
      right: 0;
      justify-content: flex-end;
      padding-right: 4px;
      background: linear-gradient(to left, white 55%, rgba(255, 255, 255, 0));
    }
  }

  .product-tab {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 4px;
    height: 38px;
    padding: 0 9px;
    font-size: 13px;
    font-weight: 500;
    color: $pb-muted;
    white-space: nowrap;
    transition: color 0.15s;

    i {
      font-size: 16px;
    }

    @media (max-width: 767px) {
      height: 42px;
      padding: 0 10px;
      font-size: 14px;
    }

    &::after {
      content: "";
      position: absolute;
      right: 6px;
      bottom: 0;
      left: 6px;
      height: 2px;
      border-radius: 2px;
      background: transparent;
    }

    &:hover {
      color: $pb-text;
    }

    &:focus-visible {
      outline: 2px solid $link-color-1;
      outline-offset: -4px;
      border-radius: 8px;
    }

    &.is-active {
      font-weight: 600;
      color: $link-color-1;

      &::after {
        background: $link-color-1;
      }
    }
  }

  /* ---------- Botones ---------- */
  .pb-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    i {
      font-size: 18px;
      line-height: 1;
    }

    &:hover:not(:disabled) {
      border-color: #9ca3af;
      background: #f9fafb;
      color: $pb-text;
    }

    &:focus-visible {
      outline: 2px solid $link-color-1;
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 767px) {
      height: 42px;
    }
  }

  .pb-btn-primary {
    border-color: $link-color-1;
    background: $link-color-1;
    color: white;

    &:hover:not(:disabled) {
      border-color: darken($link-color-1, 6%);
      background: darken($link-color-1, 6%);
      color: white;
    }
  }

  .pb-btn-success {
    border-color: #16a34a;
    background: #16a34a;
    color: white;

    &:hover:not(:disabled) {
      border-color: #15803d;
      background: #15803d;
      color: white;
    }
  }

  .pb-btn-warning {
    border-color: #fcd34d;
    background: #fffbeb;
    color: #92400e;

    &:hover:not(:disabled) {
      border-color: #f59e0b;
      background: #fef3c7;
      color: #78350f;
    }
  }

  .pb-btn-danger {
    border-color: #fca5a5;
    color: #b91c1c;

    &:hover:not(:disabled) {
      border-color: #f87171;
      background: #fef2f2;
      color: #991b1b;
    }
  }

  .pb-btn-ai {
    border-color: #6c63ff;
    background: #1e1e2e;
    color: #c4b5fd;

    &:hover:not(:disabled),
    &.is-open {
      border-color: #6c63ff;
      background: #6c63ff;
      color: white;
    }
  }

  .ai-toggle {
    flex-shrink: 0;

    @media (max-width: 767px) {
      width: 42px;
      padding: 0;

      span {
        display: none;
      }
    }
  }

  .pb-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 36px;
    font-size: 14px;
    font-weight: 600;
    color: $link-color-1;

    i {
      font-size: 18px;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  .count-pill {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: $pb-accent-soft;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
  }

  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 17px;
    font-weight: 400;
    color: $pb-muted;

    &:hover {
      background: $pb-canvas;
      color: $link-color-1;
    }
  }

  /* ---------- Contenido ---------- */
  .product-view {
    width: 100%;
    padding: 12px 16px 24px;

    @media (max-width: 767px) {
      padding: 8px 8px 24px;
    }
  }

  .product-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
  }

  .product-alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;
    padding: 8px 12px;
    border: 1px solid;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.35;

    i {
      flex-shrink: 0;
      font-size: 18px;
      line-height: 1.1;
    }

    &.is-inline {
      margin: 4px 0 0;
    }

    &.is-error {
      border-color: #fecaca;
      background: #fef2f2;
      color: #991b1b;
    }

    &.is-warning {
      border-color: #fde68a;
      background: #fffbeb;
      color: #92400e;
    }
  }

  .product-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    padding: 10px 12px;
    background: white;
    border: 1px solid $pb-border;
    border-radius: 12px;
    box-shadow: $pb-shadow;

    @media (max-width: 767px) {
      padding: 8px;
    }
  }

  .toolbar-group {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    min-width: 0;

    &.toolbar-end {
      margin-left: auto;
    }

    @media (max-width: 767px) {
      width: 100%;

      > .pb-btn {
        flex: 1 1 auto;
      }

      &.toolbar-end {
        margin-left: 0;
      }
    }
  }

  .status-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;

    .is-on {
      color: #15803d;
    }

    .is-off {
      color: #b91c1c;
    }
  }

  // Etiquetas: un grupo segmentado en vez de tres botones negros
  .label-group {
    display: inline-flex;
    align-items: stretch;
    height: 36px;
    overflow: hidden;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: white;

    .label-group-title {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 0 10px;
      background: #f9fafb;
      font-size: 13px;
      font-weight: 500;
      color: $pb-muted;

      i {
        font-size: 17px;
      }
    }

    button {
      padding: 0 12px;
      border-left: 1px solid #e5e7eb;
      font-size: 14px;
      font-weight: 500;
      color: #374151;

      &:hover {
        background: $pb-accent-soft;
        color: $link-color-1;
      }

      &:focus-visible {
        outline: 2px solid $link-color-1;
        outline-offset: -2px;
      }
    }

    @media (max-width: 767px) {
      flex: 1 1 100%;
      height: 42px;

      button {
        flex: 1;
      }
    }
  }

  // Tarjetas: .box / .box-header globales (gris con texto blanco) se ven como
  // tarjetas blancas
  .product-view .box {
    min-width: 0;
    background: white;
    border: 1px solid $pb-border;
    border-radius: 12px;
    box-shadow: $pb-shadow;
  }

  .product-view .box-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 48px;
    padding: 8px 14px;
    background: transparent;
    border-bottom: 1px solid $pb-border;
    border-radius: 12px 12px 0 0;
    font-size: 15px;
    font-weight: 600;
    color: $pb-text;
  }

  .product-view .box-content-block {
    padding: 12px 14px;

    @media (max-width: 767px) {
      padding: 10px;
    }
  }

  .editing-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    background: #fef3c7;
    font-size: 12px;
    font-weight: 600;
    color: #92400e;
  }

  .section-title {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 16px 0 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $pb-muted;

    span {
      font-weight: 700;
      letter-spacing: 0;
      text-transform: none;
      color: $pb-text;
    }
  }

  .table-scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid $pb-border;
    border-radius: 10px;
    -webkit-overflow-scrolling: touch;
  }

  table.default-table {
    width: 100%;
    border-radius: 0;
    background: transparent;
    font-size: 14px;

    thead {
      height: auto;
      background: #f9fafb;

      th {
        height: auto;
        padding: 8px 12px;
        border-bottom: 1px solid $pb-border;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
        color: $pb-muted;
      }
    }

    tr {
      height: auto;
      font-size: 14px;
      line-height: 1.4;
      color: #374151;
      cursor: default;
    }

    td {
      height: auto !important;
      padding: 8px 12px;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: middle;
    }

    tbody tr:nth-child(even) {
      background-color: transparent;
    }
  }

  .keyword-chip {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    margin: 0 6px 6px 0;
    padding: 2px 10px;
    border: 1px solid $pb-border;
    border-radius: 999px;
    background: #f9fafb;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  .keyword-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    margin-right: -6px;
    font-size: 15px;
    color: $pb-muted;

    &:hover {
      background: #fee2e2;
      color: #b91c1c;
    }
  }

  .similar-products {
    margin-top: 6px;
    overflow: hidden;
    border: 1px solid $pb-border;
    border-radius: 10px;
    background: #f9fafb;

    > .box-content-block + .box-content-block {
      border-top: 1px solid $pb-border;
    }
  }

  .similar-code {
    font-size: 14px;
    font-weight: 600;
    color: $link-color-1;
    text-align: left;

    &:hover {
      text-decoration: underline;
    }
  }

  .similar-description {
    margin: 4px 0;
    font-size: 14px;
    color: #374151;
  }

  .similar-empty {
    font-size: 13px;
    color: #b91c1c;
  }

  .kit-summary {
    margin-top: 8px;
    padding: 8px 12px;
    border: 1px solid $pb-border;
    border-radius: 10px;
    background: #f9fafb;
    font-size: 14px;
  }

  .long-description {
    padding: 12px;
    overflow-x: auto;
    border: 1px solid $pb-border;
    border-radius: 10px;
    background: #f9fafb;
    font-size: 15px;
    line-height: 1.5;

    img {
      height: auto;
      max-width: 100%;
    }
  }

  .shipping-config {
    margin-top: 16px;
    padding: 12px;
    border: 1px solid $pb-border;
    border-radius: 10px;
    background: #f9fafb;
    font-size: 14px;

    &.is-editing {
      border-color: #bfdbfe;
      background: #eff6ff;
    }

    label {
      font-weight: 400;
      line-height: 1.4;
    }
  }

  .shipping-config-title {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $pb-muted;
  }

  // Inputs del formulario de edición
  .product-view {
    input.bordered,
    select.bordered,
    input.regular.clear {
      width: 100%;
      min-height: 38px;
      margin-bottom: 0;
      padding: 6px 10px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background-color: white;
      box-shadow: none;
      font-size: 15px;
      color: $pb-text;
      transition: border-color 0.15s, box-shadow 0.15s;

      &:focus {
        outline: none;
        border-color: $link-color-1;
        box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.12);
      }

      // iOS hace zoom en inputs con letra menor a 16px
      @media (max-width: 767px) {
        min-height: 42px;
        font-size: 16px;
      }
    }

    input[type="checkbox"] {
      accent-color: $link-color-1;
    }
  }

  /* Barra fija de guardado mientras se edita */
  .edit-bar {
    position: sticky;
    bottom: 0;
    z-index: 30;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 12px;
    margin: 0 -4px;
    padding: 10px 12px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom));
    background: white;
    border: 1px solid $pb-border;
    border-radius: 12px;
    box-shadow: 0 -6px 20px rgba(16, 24, 40, 0.12);
    animation: pb-fade-in 0.15s ease-out;

    .edit-bar-text {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-weight: 600;
      color: #92400e;
    }

    .edit-bar-actions {
      display: flex;
      gap: 8px;
      margin-left: auto;
    }

    @media (max-width: 767px) {
      margin: 0 -8px;
      border-radius: 12px 12px 0 0;

      .edit-bar-actions {
        width: 100%;

        .pb-btn {
          flex: 1;
        }
      }
    }
  }

  // Tablas de precio y stock (tiny-table también la usa ProductStockAndPrice):
  // dentro del modal, cabecera clara en vez de negra y zebra suave
  .product-view .tiny-table {
    border-collapse: collapse;
    font-size: 14px;

    tr:nth-child(2n),
    tr:nth-child(2n + 1) {
      background-color: white;
    }

    tr:nth-child(2n) {
      background-color: #f9fafb;
    }

    tr:nth-child(1) {
      background-color: #f3f4f6;
      color: #374151;
    }

    th {
      padding: 8px 10px;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      color: $pb-muted;
    }

    td {
      padding: 7px 10px;
      border-top: 1px solid #f3f4f6;
    }
  }

  /* ---------- Estados ---------- */
  .product-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    max-width: 640px;
    margin: 48px auto;
    padding: 32px 24px;
    background: white;
    border: 1px solid $pb-border;
    border-radius: 16px;
    text-align: center;

    @media (max-width: 767px) {
      margin: 16px 8px;
      padding: 24px 16px;
    }

    .state-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #fef2f2;
      font-size: 30px;
      color: #dc2626;
    }

    .state-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: $pb-text;
    }

    .state-text {
      margin: 0;
      font-size: 14px;
      color: $pb-muted;

      strong {
        color: $pb-text;
      }
    }
  }

  .loading-full {
    min-height: 60vh;
  }
}

@keyframes pb-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
<style lang="postcss" scoped>
label {
  font-weight: bold;
}
.info-field {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 3px 0;
  font-size: 14px;
  line-height: 1.4;
  .info-label {
    color: #666;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    &::after {
      content: ":";
    }
  }
  .info-value {
    color: #111;
  }
  .info-meta {
    color: #6b7280;
    font-size: 12px;
    font-weight: normal;
  }
}
.copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;
  margin-left: 4px;
  padding: 0 6px;
  font-size: 16px;
  line-height: 1;
  color: #4b5563;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  vertical-align: middle;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #ddd;
    color: #111;
  }
}
.similar-meta {
  font-size: 13px;
  color: #555;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.stats-wrap {
  padding: 0;
}
.stats-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.stats-empty {
  text-align: center;
  color: #888;
  padding: 40px 0;
}
.stats-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  @media (min-width: 600px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.stat-card {
  border-radius: 12px;
  padding: 14px 16px;
  background: #f5f5f5;
  .stat-label {
    font-size: 12px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #666;
    margin-bottom: 6px;
  }
  .stat-value {
    font-size: 20px;
    font-weight: 700;
    overflow-wrap: anywhere;
    @media (max-width: 480px) {
      font-size: 17px;
    }
  }
  @media (max-width: 480px) {
    padding: 12px;
  }
  &.green {
    background: #e6f7ee;
    .stat-value {
      color: #1a7a4a;
    }
  }
  &.blue {
    background: #e6f0fb;
    .stat-value {
      color: #1a4a8a;
    }
  }
  &.gray {
    background: #f0f0f0;
    .stat-value {
      color: #444;
    }
  }
  &.red {
    background: #fdecea;
    .stat-value {
      color: #a81c1c;
    }
  }
}
.stats-table-wrap {
  overflow-x: auto;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}
.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  th {
    background: #f9fafb;
    padding: 8px 12px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
    white-space: nowrap;
  }
  td {
    padding: 7px 12px;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
  }
  tr.current-year td {
    background: #fff8e1;
    font-weight: 600;
  }

  tbody tr:hover td {
    background: #eef4ff;
  }
}

.orders-count {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 8px;
}
.product-orders-table {
  tbody tr {
    cursor: pointer;
  }
  .order-link {
    color: #126e6a;
    font-weight: 600;
  }
  .order-status {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    &.tone-green {
      background: #dcfce7;
      color: #166534;
    }
    &.tone-red {
      background: #fee2e2;
      color: #991b1b;
    }
    &.tone-gray {
      background: #f3f4f6;
      color: #4b5563;
    }
    &.tone-blue {
      background: #e0ecfb;
      color: #1a4a8a;
    }
  }
}
.orders-more {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.product-lines-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 5px 10px;
    border: 1px solid #e5e7eb;
    vertical-align: top;
  }

  th {
    width: 36%;
    min-width: 96px;
    text-align: left;
    font-weight: 600;
    color: #4b5563;
    background: #f3f4f6;
  }

  tbody tr:nth-child(even) td {
    background: #fafafa;
  }

  tbody tr:hover th,
  tbody tr:hover td {
    background: #eef4ff;
  }
}

.sidebar-details {
  .info-field {
    display: grid;
    grid-template-columns: minmax(120px, 48%) minmax(0, 1fr);
    gap: 0;
    align-items: start;
    margin: 0;
    padding: 0;
    border-bottom: 1px solid #e5e7eb;
  }

  .info-field:nth-of-type(odd) {
    background: #fafafa;
  }

  .info-field:hover {
    background: #eef4ff;
  }

  .info-label {
    font-weight: 700;
    padding: 5px 10px;
    border-right: 1px solid #e5e7eb;
  }

  .info-value {
    padding: 5px 10px;
  }
}

.summary-table-block {
  label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  td {
    border: 1px solid #e5e7eb;
    padding: 7px 8px;
    vertical-align: top;
  }

  tr:nth-child(even) td {
    background: #fafafa;
  }

  tr:hover td {
    background: #eef4ff;
  }

  td:first-child {
    width: 48%;
    font-weight: 600;
    color: #374151;
  }
}

.default-table {
  tbody tr:hover td {
    background: #eef4ff;
  }
}

.tiny-table {
  tr:hover td {
    background: #eef4ff;
  }
}

.summary-empty {
  text-align: center;
  color: #9ca3af;
  font-weight: 500;
}

.ai-toggle-btn {
  background: #1e1e2e;
  border: 1px solid #2a2a3e;
  color: #a78bfa;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  letter-spacing: 0.3px;

  &:hover {
    background: #2a2a3e;
    border-color: #a78bfa;
  }

  &--active {
    background: #6c63ff;
    border-color: #6c63ff;
    color: white;
  }
}
</style>
